/* =====================================================================================
   Goals, appraisal and evaluation.

   The design constraint, which came from you: people should be doing the job, not spending
   time looking like they are doing the job. So:

     * five objectives maximum, enforced by a trigger in the database. A list of twelve
       priorities is a list of none.
     * evidence is gathered FROM WORK ALREADY RECORDED. If the system can see it, the system
       fills it in.
     * what it cannot see stays blank. Activity counts are not achievement, and someone who
       spent a fortnight on one regulatory submission will show almost nothing here. The
       interface says so, rather than letting a low count imply a low contribution.

   Nothing here reads hours, logins or keystrokes. GDPR-09 rules those out and they measure
   presence rather than output, which is the failure this module is meant to avoid.
   ===================================================================================== */

import { BACKEND } from './db';

export interface Objective {
  id: string;
  employee_id: string;
  cycle_id: string | null;
  title: string;
  description: string | null;
  measure: string | null;
  target: string | null;
  target_date: string | null;
  weight: number | null;
  status: string;
  metric_key: string | null;
  self_rating: number | null;
  manager_rating: number | null;
  outcome_note: string | null;
}

export interface Evidence {
  out_module: string;
  out_headline: string;
  out_detail: string | null;
  out_occurred: string | null;
  out_count: number;
}

export interface Cycle {
  id: string; name: string; period_start: string; period_end: string; status: string;
}

/* Exactly the domain hr.objectives allows. */
export const OBJECTIVE_STATUS = ['draft', 'active', 'achieved',
  'partially_achieved', 'not_achieved', 'cancelled'] as const;

const live = () => BACKEND === 'supabase';
function tidy(m: string) { return m.replace(/^.*?(?:ERROR|error):\s*/i, '').trim() || m; }

async function orgId(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}

/** Evidence assembled from recorded work. The whole point of the module. */
export async function gatherEvidence(memberId: string, from: string, to: string): Promise<Evidence[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('gather_evidence', {
    p_member_id: memberId, p_from: from, p_to: to,
  });
  if (error) throw new Error(tidy(error.message));
  return data ?? [];
}

export async function fetchCycles(): Promise<Cycle[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').rpc('appraisal_cycles');
  return (data ?? []).map((c: any) => ({
    id: c.out_id, name: c.out_name, period_start: c.out_period_start,
    period_end: c.out_period_end, status: c.out_status,
  }));
}

export async function fetchObjectives(employeeId: string): Promise<Objective[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').rpc('appraisal_objectives', {
    p_employee_id: employeeId,
  });
  if (error) throw new Error(tidy(error.message));
  return (data ?? []).map((r: any) => ({
    id: r.out_id, employee_id: r.out_employee_id, cycle_id: r.out_cycle_id,
    title: r.out_title, description: r.out_description, measure: r.out_measure,
    target: r.out_target, target_date: r.out_due_on, weight: r.out_weighting,
    status: r.out_status, metric_key: r.out_metric_key,
    outcome_note: r.out_outcome_note, self_rating: null, manager_rating: null,
  }));
}

/** The signed-in person's own employee record, so Goals opens on their own objectives. */
export async function myEmployeeId(): Promise<string | null> {
  if (!live()) return null;
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').rpc('my_employee_id');
  return (data as string) ?? null;
}

export async function addObjective(o: {
  employeeId: string; title: string; description?: string;
  measure?: string; targetDate?: string; weight?: number;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').rpc('appraisal_add_objective', {
    p_employee_id: o.employeeId, p_title: o.title,
    p_description: o.description ?? null, p_measure: o.measure ?? null,
    p_target_date: o.targetDate ?? null, p_weight: o.weight ?? null,
  });
  if (error) throw new Error(tidy(error.message));
}

export async function updateObjective(id: string, patch: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').rpc('appraisal_update_objective', {
    p_objective_id: id, p_patch: patch,
  });
  if (error) throw new Error(tidy(error.message));
}

/** Attach a piece of gathered evidence to an objective, so the link is explicit. */
export async function attachEvidence(objectiveId: string, e: {
  module: string; headline: string; detail?: string; occurredOn?: string; origin?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('objective_evidence').insert({
    org_id: await orgId(), objective_id: objectiveId,
    origin: e.origin || 'auto', source_module: e.module,
    headline: e.headline, detail: e.detail ?? null,
    occurred_on: e.occurredOn ?? null,
  });
  if (error && error.code !== '23505') throw new Error(tidy(error.message));
}

export async function fetchEvidenceFor(objectiveId: string) {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('objective_evidence')
    .select('*').eq('objective_id', objectiveId).order('occurred_on', { ascending: false });
  return data ?? [];
}
